import { useState, useEffect, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ChatConversationWithMessages, ChatMessage as ApiChatMessage } from '@/api/generated/client/models';

export interface UseChatConversationOptions {
  claimId: number;
  enabled?: boolean;
}

export interface UseChatConversationReturn {
  conversation: ChatConversationWithMessages | null;
  messages: ApiChatMessage[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  sendMessage: (content: string) => Promise<void>;
  isSending: boolean;
  createConversation: () => Promise<void>;
  isCreating: boolean;
  startNewConversation: () => Promise<void>;
}

export function useChatConversation({
  claimId,
  enabled = true,
}: UseChatConversationOptions): UseChatConversationReturn {
  const queryClient = useQueryClient();
  const [conversationId, setConversationId] = useState<number | null>(null);

  // Fetch or create conversation for this claim
  const {
    data: conversationsData,
    isLoading: isLoadingConversations,
    isError: isConversationsError,
    error: conversationsError,
  } = useQuery({
    queryKey: ['chat-conversations', claimId],
    queryFn: async () => {
      const response = await apiClient.chat.getChatConversations({ page: 1, limit: 100 });
      return response;
    },
    enabled,
  });

  // Find conversation for this claim
  useEffect(() => {
    if (conversationsData?.conversations) {
      const claimConversation = conversationsData.conversations.find(
        (c) => c.claimId === claimId
      );
      if (claimConversation) {
        setConversationId(claimConversation.id);
      }
    }
  }, [conversationsData, claimId]);

  // Fetch conversation with messages
  const {
    data: conversation,
    isLoading: isLoadingConversation,
    isError: isConversationError,
    error: conversationError,
  } = useQuery({
    queryKey: ['chat-conversation', conversationId],
    queryFn: async () => {
      if (!conversationId) return null;
      return await apiClient.chat.getChatConversation({ id: conversationId });
    },
    enabled: enabled && conversationId !== null,
  });

  // Create conversation mutation
  const createConversationMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.chat.createChatConversation({
        createConversationRequest: {
          claimId,
          title: `Claim #${claimId} Discussion`,
        },
      });
      return response;
    },
    onSuccess: (data) => {
      setConversationId(data.id);
      queryClient.invalidateQueries({ queryKey: ['chat-conversations', claimId] });
    },
  });

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: async (content: string) => {
      if (!conversationId) {
        throw new Error('No active conversation');
      }
      return await apiClient.chat.sendChatMessage({
        id: conversationId,
        sendMessageRequest: { content },
      });
    },
    onMutate: async (content: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['chat-conversation', conversationId] });

      // Snapshot the previous value
      const previousConversation = queryClient.getQueryData<ChatConversationWithMessages>([
        'chat-conversation',
        conversationId,
      ]);

      // Optimistically update to show user message immediately
      if (previousConversation && conversationId) {
        const optimisticMessage: ApiChatMessage = {
          id: Date.now(), // Temporary ID
          conversationId,
          role: 'user',
          content,
          createdAt: new Date(),
          metadata: undefined,
        };

        queryClient.setQueryData<ChatConversationWithMessages>(
          ['chat-conversation', conversationId],
          {
            ...previousConversation,
            messages: [...(previousConversation.messages || []), optimisticMessage],
          }
        );
      }

      // Return context with previous data for rollback on error
      return { previousConversation };
    },
    onError: (err, content, context) => {
      // Rollback to previous data on error
      if (context?.previousConversation && conversationId) {
        queryClient.setQueryData(
          ['chat-conversation', conversationId],
          context.previousConversation
        );
      }
      
      // Log error for debugging
      console.error('Error sending message:', err);
    },
    onSuccess: () => {
      // Invalidate and refetch conversation to get new messages (including AI response)
      queryClient.invalidateQueries({ queryKey: ['chat-conversation', conversationId] });
    },
  });

  const sendMessage = useCallback(
    async (content: string) => {
      if (!conversationId) {
        // Create conversation first if it doesn't exist
        await createConversationMutation.mutateAsync();
      }
      await sendMessageMutation.mutateAsync(content);
    },
    [conversationId, createConversationMutation, sendMessageMutation]
  );

  const createConversation = useCallback(async () => {
    await createConversationMutation.mutateAsync();
  }, [createConversationMutation]);

  const startNewConversation = useCallback(async () => {
    // Clear current conversation and create a new one
    setConversationId(null);
    await createConversationMutation.mutateAsync();
  }, [createConversationMutation]);

  const isLoading = isLoadingConversations || isLoadingConversation;
  const isError = isConversationsError || isConversationError;
  const error = conversationsError || conversationError;

  return {
    conversation: conversation || null,
    messages: conversation?.messages || [],
    isLoading,
    isError,
    error: error as Error | null,
    sendMessage,
    isSending: sendMessageMutation.isPending,
    createConversation,
    isCreating: createConversationMutation.isPending,
    startNewConversation,
  };
}
